import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import {
  dealsApi,
  type AuState,
  type BusinessStructure,
  type Contact,
  type ContactInput,
  type ContactStatus,
  type ContactMethod,
  type UserOption,
} from "@/lib/api"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { AbnField } from "./AbnField"

type Props = {
  initial?: Partial<Contact>
  brokers: UserOption[]
  onSubmit: (data: ContactInput) => Promise<void>
  onCancel: () => void
  submitLabel?: string
  isSubmitting?: boolean
}

const AU_STATES: AuState[] = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"]
const STATUSES: { value: ContactStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "active", label: "Active" },
  { value: "client", label: "Client" },
  { value: "inactive", label: "Inactive" },
]
const STRUCTURES: { value: BusinessStructure; label: string }[] = [
  { value: "sole_trader", label: "Sole Trader" },
  { value: "partnership", label: "Partnership" },
  { value: "company", label: "Company" },
  { value: "trust", label: "Trust" },
  { value: "individual", label: "Individual" },
]
const CONTACT_METHODS: { value: ContactMethod; label: string }[] = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
]

export function ContactForm({
  initial,
  brokers,
  onSubmit,
  onCancel,
  submitLabel = "Create Contact",
  isSubmitting = false,
}: Props) {
  const [form, setForm] = useState<ContactInput>({
    status: initial?.status ?? "new",
    firstName: initial?.firstName ?? "",
    lastName: initial?.lastName ?? "",
    title: initial?.title ?? "",
    email: initial?.email ?? "",
    mobile: initial?.mobile ?? "",
    businessPhone: initial?.businessPhone ?? "",
    dateOfBirth: initial?.dateOfBirth ?? "",
    occupation: initial?.occupation ?? "",
    businessName: initial?.businessName ?? "",
    abn: initial?.abn ?? "",
    acn: initial?.acn ?? "",
    businessStructure: initial?.businessStructure ?? null,
    industry: initial?.industry ?? "",
    financeType: initial?.financeType ?? "",
    website: initial?.website ?? "",
    coApplicantTitle: initial?.coApplicantTitle ?? "",
    coApplicantFirstName: initial?.coApplicantFirstName ?? "",
    coApplicantLastName: initial?.coApplicantLastName ?? "",
    coApplicantEmail: initial?.coApplicantEmail ?? "",
    coApplicantMobile: initial?.coApplicantMobile ?? "",
    addressStreet: initial?.addressStreet ?? "",
    addressSuburb: initial?.addressSuburb ?? "",
    addressState: initial?.addressState ?? null,
    addressPostcode: initial?.addressPostcode ?? "",
    preferredContactMethod: initial?.preferredContactMethod ?? null,
    bestContactTime: initial?.bestContactTime ?? "",
    source: initial?.source ?? "",
    notes: initial?.notes ?? "",
    ownerId: initial?.ownerId ?? null,
    tags: initial?.tags ?? [],
    marketingConsent: initial?.marketingConsent ?? false,
    emailOptOut: initial?.emailOptOut ?? false,
    smsOptOut: initial?.smsOptOut ?? false,
  })
  const [showCoApplicant, setShowCoApplicant] = useState(!!initial?.coApplicantFirstName)
  const [tagsInput, setTagsInput] = useState((initial?.tags ?? []).join(", "))

  // Shared Lead Source list — same pool as the deal enquiry form and deal
  // detail card. Adding one here becomes visible everywhere else too.
  const leadSourcesQ = useQuery({
    queryKey: ["deals-lead-sources"],
    queryFn: () => dealsApi.leadSources(),
    staleTime: 60_000,
  })
  const leadSourceOptions =
    leadSourcesQ.data?.sources.map((s) => ({
      value: s.value,
      label: s.value,
    })) ?? []

  useEffect(() => {
    setForm((f) => ({
      ...f,
      tags: tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    }))
  }, [tagsInput])

  const set = <K extends keyof ContactInput>(k: K, v: ContactInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Clean nulls from empty strings
    const cleaned: ContactInput = { ...form }
    ;(Object.keys(cleaned) as (keyof ContactInput)[]).forEach((k) => {
      const v = cleaned[k]
      if (v === "") (cleaned as Record<string, unknown>)[k] = null
    })
    await onSubmit(cleaned)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Primary applicant */}
      <section className="space-y-4">
        <h3 className="text-foreground text-sm font-semibold">Primary Applicant</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={form.title ?? ""}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Mr / Ms / Dr"
            />
          </div>
          <div>
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              required
              value={form.firstName}
              onChange={(e) => set("firstName", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              required
              value={form.lastName}
              onChange={(e) => set("lastName", e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => set("email", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="mobile">Mobile</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="04XX XXX XXX"
              value={form.mobile ?? ""}
              onChange={(e) => set("mobile", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="businessPhone">Business Phone</Label>
            <Input
              id="businessPhone"
              type="tel"
              value={form.businessPhone ?? ""}
              onChange={(e) => set("businessPhone", e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Input
              id="dateOfBirth"
              type="date"
              value={form.dateOfBirth ?? ""}
              onChange={(e) => set("dateOfBirth", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="occupation">Occupation</Label>
            <Input
              id="occupation"
              value={form.occupation ?? ""}
              onChange={(e) => set("occupation", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="preferredContactMethod">Preferred Contact</Label>
            <select
              id="preferredContactMethod"
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={form.preferredContactMethod ?? ""}
              onChange={(e) =>
                set("preferredContactMethod", (e.target.value || null) as ContactMethod | null)
              }
            >
              <option value="">—</option>
              {CONTACT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Business */}
      <section className="space-y-4">
        <h3 className="text-foreground text-sm font-semibold">Business Details</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="businessName">Business Name</Label>
            <Input
              id="businessName"
              value={form.businessName ?? ""}
              onChange={(e) => set("businessName", e.target.value)}
            />
          </div>
          <AbnField
            value={form.abn ?? ""}
            onChange={(v) => set("abn", v)}
            onLookupSuccess={(r) => {
              // Auto-fill business name and state if empty
              if (!form.businessName) set("businessName", r.businessName)
              if (!form.addressState && r.state) {
                const upper = r.state.toUpperCase()
                if (AU_STATES.includes(upper as AuState)) set("addressState", upper as AuState)
              }
            }}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="businessStructure">Structure</Label>
            <select
              id="businessStructure"
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={form.businessStructure ?? ""}
              onChange={(e) =>
                set("businessStructure", (e.target.value || null) as BusinessStructure | null)
              }
            >
              <option value="">—</option>
              {STRUCTURES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="industry">Industry</Label>
            <Input
              id="industry"
              value={form.industry ?? ""}
              onChange={(e) => set("industry", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="financeType">Finance Type</Label>
            <Input
              id="financeType"
              placeholder="Vehicle, Equipment, etc."
              value={form.financeType ?? ""}
              onChange={(e) => set("financeType", e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            value={form.website ?? ""}
            onChange={(e) => set("website", e.target.value)}
          />
        </div>
      </section>

      {/* Address */}
      <section className="space-y-4">
        <h3 className="text-foreground text-sm font-semibold">Address</h3>
        <div>
          <Label htmlFor="addressStreet">Street</Label>
          <Input
            id="addressStreet"
            value={form.addressStreet ?? ""}
            onChange={(e) => set("addressStreet", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="addressSuburb">Suburb</Label>
            <Input
              id="addressSuburb"
              value={form.addressSuburb ?? ""}
              onChange={(e) => set("addressSuburb", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="addressState">State</Label>
            <select
              id="addressState"
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={form.addressState ?? ""}
              onChange={(e) => set("addressState", (e.target.value || null) as AuState | null)}
            >
              <option value="">—</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="addressPostcode">Postcode</Label>
            <Input
              id="addressPostcode"
              maxLength={4}
              value={form.addressPostcode ?? ""}
              onChange={(e) =>
                set("addressPostcode", e.target.value.replace(/\D/g, "").slice(0, 4))
              }
            />
          </div>
        </div>
      </section>

      {/* Co-applicant (collapsible) */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-foreground text-sm font-semibold">Co-Applicant</h3>
          <button
            type="button"
            className="text-rc-blue-600 text-xs hover:underline"
            onClick={() => setShowCoApplicant(!showCoApplicant)}
          >
            {showCoApplicant ? "Remove" : "+ Add co-applicant"}
          </button>
        </div>
        {showCoApplicant && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="coApplicantTitle">Title</Label>
                <Input
                  id="coApplicantTitle"
                  value={form.coApplicantTitle ?? ""}
                  onChange={(e) => set("coApplicantTitle", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="coApplicantFirstName">First Name</Label>
                <Input
                  id="coApplicantFirstName"
                  value={form.coApplicantFirstName ?? ""}
                  onChange={(e) => set("coApplicantFirstName", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="coApplicantLastName">Last Name</Label>
                <Input
                  id="coApplicantLastName"
                  value={form.coApplicantLastName ?? ""}
                  onChange={(e) => set("coApplicantLastName", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="coApplicantEmail">Email</Label>
                <Input
                  id="coApplicantEmail"
                  type="email"
                  value={form.coApplicantEmail ?? ""}
                  onChange={(e) => set("coApplicantEmail", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="coApplicantMobile">Mobile</Label>
                <Input
                  id="coApplicantMobile"
                  type="tel"
                  value={form.coApplicantMobile ?? ""}
                  onChange={(e) => set("coApplicantMobile", e.target.value)}
                />
              </div>
            </div>
          </>
        )}
      </section>

      {/* CRM details */}
      <section className="space-y-4">
        <h3 className="text-foreground text-sm font-semibold">CRM Details</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={form.status ?? "new"}
              onChange={(e) => set("status", e.target.value as ContactStatus)}
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ownerId">Assigned Broker</Label>
            <select
              id="ownerId"
              className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              value={form.ownerId ?? ""}
              onChange={(e) => set("ownerId", e.target.value || null)}
            >
              <option value="">Unassigned</option>
              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.fullName} ({b.role})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="source">Lead Source</Label>
            <Combobox
              value={form.source ?? ""}
              onChange={(v) => set("source", v ?? "")}
              options={leadSourceOptions}
              placeholder="Where did they come from?"
              emptyText="Type a new source and press Add"
              creatable
              createLabel="Add source"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="tags">Tags (comma-separated)</Label>
          <Input
            id="tags"
            placeholder="vip, truck-finance, referral"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            rows={4}
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.marketingConsent ?? false}
              onChange={(e) => set("marketingConsent", e.target.checked)}
            />
            Marketing consent
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.emailOptOut ?? false}
              onChange={(e) => set("emailOptOut", e.target.checked)}
            />
            Email opt-out
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.smsOptOut ?? false}
              onChange={(e) => set("smsOptOut", e.target.checked)}
            />
            SMS opt-out
          </label>
        </div>
      </section>

      <div className="border-border flex justify-end gap-3 border-t pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
